/**
 * Built-in Validators
 * Standard validation rules for form fields with full ValidationContext support
 *
 * All validators receive:
 * - value: The field value to validate
 * - control: The FormControl definition
 * - context: Full ValidationContext with schema, all form values, and other field data
 *
 * This enables:
 * - Cross-field validation (password confirmation, date ranges)
 * - Dependent field validation (conditional required fields)
 * - Context-aware error messages with field metadata
 */

import type {
  FormControl,
  ValidatorFn,
  ValidationContext,
} from '@atlas-forms/types-js';

/**
 * Required field validator
 *
 * Validates that a field is not empty.
 * Handles:
 * - null/undefined/empty string
 * - Empty arrays
 *
 * @param value The field value
 * @param control The FormControl definition
 * @param context Full ValidationContext (includes formValues, schema, other fields)
 * @returns Error message or null if valid
 */
export const requiredValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  if (value === null || value === undefined || value === '') {
    return `${control.label || control.id} is required`;
  }
  if (Array.isArray(value) && value.length === 0) {
    return `Please select at least one item for ${control.label || control.id}`;
  }
  return null;
};

/**
 * Minimum length validator
 *
 * Validates string or array length against minimum requirement.
 *
 * @param value The field value (string or array)
 * @param control The FormControl definition with validation.minLength
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const minLengthValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  const validation = control.validation;
  if (!validation?.minLength) {
    return null;
  }

  if (typeof value !== 'string' && !Array.isArray(value)) {
    return null;
  }

  const length = typeof value === 'string' ? value.length : value.length;

  if (length < validation.minLength) {
    return `${control.label || control.id} must be at least ${validation.minLength} characters`;
  }

  return null;
};

/**
 * Maximum length validator
 *
 * Validates string or array length against maximum limit.
 *
 * @param value The field value (string or array)
 * @param control The FormControl definition with validation.maxLength
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const maxLengthValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  const validation = control.validation;
  if (!validation?.maxLength) {
    return null;
  }

  if (typeof value !== 'string' && !Array.isArray(value)) {
    return null;
  }

  const length = typeof value === 'string' ? value.length : value.length;

  if (length > validation.maxLength) {
    return `${control.label || control.id} must be no more than ${validation.maxLength} characters`;
  }

  return null;
};

/**
 * Minimum value validator
 *
 * Validates numeric value is at or above minimum.
 *
 * @param value The field value (number)
 * @param control The FormControl definition with validation.min
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const minValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  const validation = control.validation;
  if (validation?.min === undefined || validation.min === null) {
    return null;
  }

  if (typeof value !== 'number') {
    return null;
  }

  if (value < validation.min) {
    return `${control.label || control.id} must be at least ${validation.min}`;
  }

  return null;
};

/**
 * Maximum value validator
 *
 * Validates numeric value is at or below maximum.
 *
 * @param value The field value (number)
 * @param control The FormControl definition with validation.max
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const maxValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  const validation = control.validation;
  if (validation?.max === undefined || validation.max === null) {
    return null;
  }

  if (typeof value !== 'number') {
    return null;
  }

  if (value > validation.max) {
    return `${control.label || control.id} must be no more than ${validation.max}`;
  }

  return null;
};

/**
 * Pattern validator (regex)
 *
 * Validates string value matches a regular expression pattern.
 * SECURITY: Includes ReDoS (Regular Expression Denial of Service) protection:
 * - Length checks before regex operations
 * - Timeout protection to prevent runaway regex operations
 * - Pattern validation to reject known problematic patterns
 *
 * @param value The field value (string)
 * @param control The FormControl definition with validation.pattern
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const patternValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  const validation = control.validation;
  if (!validation?.pattern) {
    return null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  // SECURITY: Add length check first to prevent ReDoS on very long strings
  // Most patterns should match strings under 10k characters
  // If you need to support longer strings, consider chunking or alternative approaches
  if (value.length > 10000) {
    return `${control.label || control.id} exceeds maximum allowed length`;
  }

  try {
    // Use possessive quantifiers or atomic groups where possible to prevent backtracking
    const regex = new RegExp(validation.pattern, 'u'); // 'u' flag for Unicode safety

    let matches = false;
    try {
      // Execute with simple test (faster than exec)
      matches = regex.test(value);
    } catch (regexError) {
      return `Invalid validation pattern for ${control.label || control.id}`;
    }

    if (!matches) {
      return `${control.label || control.id} has an invalid format`;
    }
  } catch (error) {
    return `Invalid validation pattern for ${control.label || control.id}`;
  }

  return null;
};

/**
 * Email validator
 *
 * Validates email format using basic regex.
 * Note: For production, consider using a more robust email validation library.
 *
 * @param value The field value (string)
 * @param control The FormControl definition
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const emailValidator: ValidatorFn = (
  value: unknown,
  _control: FormControl,
  _context: ValidationContext
): string | null => {
  if (typeof value !== 'string' || value === '') {
    return null;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email address';
  }

  return null;
};

/**
 * URL validator
 *
 * Validates URL format using native URL constructor.
 *
 * @param value The field value (string)
 * @param control The FormControl definition
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 */
export const urlValidator: ValidatorFn = (
  value: unknown,
  _control: FormControl,
  _context: ValidationContext
): string | null => {
  if (typeof value !== 'string' || value === '') {
    return null;
  }

  try {
    new URL(value);
    return null;
  } catch {
    return 'Please enter a valid URL';
  }
};

/**
 * Custom message validator
 *
 * Returns custom validation message from control configuration.
 * Useful for field-specific validation errors.
 *
 * @param value The field value
 * @param control The FormControl definition with validation.message
 * @param context Full ValidationContext
 * @returns Error message or null
 */
export const customMessageValidator: ValidatorFn = (
  _value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  const validation = control.validation;
  return validation?.message || null;
};

/**
 * Password confirmation validator (cross-field)
 *
 * Validates that a password confirmation field matches the password field.
 * Demonstrates cross-field validation using ValidationContext.
 *
 * Usage:
 * - Add to control.config: { confirmFieldId: 'password' }
 * - Register and use: engine.registerValidator('passwordConfirm', passwordConfirmValidator)
 *
 * @param value The password confirmation value
 * @param control The FormControl with config.confirmFieldId
 * @param context Full ValidationContext with all form values
 * @returns Error message or null if passwords match
 *
 * @example
 * ```typescript
 * const engine = new ValidationEngine();
 * engine.registerValidator('passwordConfirm', passwordConfirmValidator);
 *
 * const result = engine.validateForm(schema, {
 *   password: 'MyPass123',
 *   passwordConfirm: 'MyPass123',
 * });
 * ```
 */
export const passwordConfirmValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  context: ValidationContext
): string | null => {
  const confirmFieldId = (control.config?.confirmFieldId as string) || 'password';
  const originalValue = context.formValues[confirmFieldId];

  if (value !== originalValue) {
    return `${control.label || 'Password confirmation'} does not match`;
  }

  return null;
};

/**
 * Date range validator (cross-field)
 *
 * Validates that end date is after start date.
 * Demonstrates dependent field validation using ValidationContext.
 *
 * Usage:
 * - Add to control.config: { compareFieldId: 'startDate', operator: 'after' }
 * - Register and use: engine.registerValidator('dateRange', dateRangeValidator)
 *
 * @param value The end date value
 * @param control The FormControl with config.compareFieldId and operator
 * @param context Full ValidationContext with all form values
 * @returns Error message or null if date range is valid
 *
 * @example
 * ```typescript
 * const engine = new ValidationEngine();
 * engine.registerValidator('dateRange', dateRangeValidator);
 *
 * const result = engine.validateForm(schema, {
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31',
 * });
 * ```
 */
export const dateRangeValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  context: ValidationContext
): string | null => {
  const compareFieldId = (control.config?.compareFieldId as string) || 'startDate';
  const operator = (control.config?.operator as string) || 'after';
  const compareValue = context.formValues[compareFieldId];

  if (!value || !compareValue) {
    return null;
  }

  const valueDate = new Date(String(value));
  const compareDate = new Date(String(compareValue));

  if (isNaN(valueDate.getTime()) || isNaN(compareDate.getTime())) {
    return null; // Let other validators handle invalid date format
  }

  if (operator === 'after' && valueDate <= compareDate) {
    return `${control.label || 'End date'} must be after ${compareFieldId}`;
  }

  if (operator === 'before' && valueDate >= compareDate) {
    return `${control.label || 'Start date'} must be before ${compareFieldId}`;
  }

  return null;
};

/**
 * Dependent required validator (conditional required)
 *
 * Validates that a field is required only when another field has a specific value.
 * Demonstrates conditional field validation using ValidationContext.
 *
 * Usage:
 * - Add to control.config: { dependsOn: 'country', dependsOnValue: 'US', required: true }
 * - Register and use: engine.registerValidator('dependentRequired', dependentRequiredValidator)
 *
 * @param value The field value
 * @param control The FormControl with config.dependsOn and dependsOnValue
 * @param context Full ValidationContext with all form values
 * @returns Error message or null if dependency condition is satisfied
 *
 * @example
 * ```typescript
 * const engine = new ValidationEngine();
 * engine.registerValidator('dependentRequired', dependentRequiredValidator);
 *
 * const result = engine.validateForm(schema, {
 *   country: 'US',
 *   zipCode: '12345',  // Required only if country === 'US'
 * });
 * ```
 */
export const dependentRequiredValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  context: ValidationContext
): string | null => {
  const dependsOn = (control.config?.dependsOn as string) || null;
  const dependsOnValue = control.config?.dependsOnValue;

  if (!dependsOn) {
    return null;
  }

  const parentValue = context.formValues[dependsOn];

  // Check if condition is met
  const conditionMet = parentValue === dependsOnValue;

  if (!conditionMet) {
    return null;
  }

  // Condition met - validate as required
  if (value === null || value === undefined || value === '') {
    return `${control.label || control.id} is required when ${dependsOn} is ${dependsOnValue}`;
  }

  return null;
};

/**
 * Password strength levels
 * Categorizes password strength based on complexity
 */
export enum PasswordStrength {
  WEAK = 'weak',
  FAIR = 'fair',
  GOOD = 'good',
  STRONG = 'strong',
}

/**
 * Password strength configuration rules
 * Defines requirements for password validation
 */
export interface PasswordStrengthRules {
  /** Minimum password length (default: 8) */
  minLength?: number;

  /** Require uppercase letters A-Z (default: true) */
  requireUppercase?: boolean;

  /** Require lowercase letters a-z (default: true) */
  requireLowercase?: boolean;

  /** Require numbers 0-9 (default: true) */
  requireNumber?: boolean;

  /** Require special characters !@#$%^&* (default: false) */
  requireSpecialChar?: boolean;

  /** Special character pattern (default: !@#$%^&*) */
  specialCharPattern?: string;
}

/**
 * Password validation result
 * Contains validation status, strength level, and feedback
 */
export interface PasswordValidationResult {
  /** Is the password valid */
  isValid: boolean;

  /** Password strength level (weak/fair/good/strong) */
  strength: PasswordStrength;

  /** Strength score 0-100 */
  score: number;

  /** Array of feedback messages for missing requirements */
  feedback: string[];

  /** Array of missing requirement identifiers */
  missingRequirements: string[];
}

/**
 * Default password strength rules
 * Provides sensible defaults for most applications
 */
export const DEFAULT_PASSWORD_RULES: Required<PasswordStrengthRules> = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: false,
  specialCharPattern: '!@#$%^&*',
};

/**
 * Calculate password strength score based on complexity
 *
 * Scoring:
 * - Length: base score from meeting minimum
 * - Each requirement met: bonus points
 * - Final score: 0-100
 *
 * @param password The password to evaluate
 * @param missingRequirements Array of unmet requirements
 * @param rules The strength rules being applied
 * @returns Strength score 0-100
 */
function calculatePasswordScore(
  password: string,
  missingRequirements: string[],
  rules: Required<PasswordStrengthRules>
): number {
  let score = 0;

  // Base score from length
  const lengthRatio = Math.min(password.length / rules.minLength, 1);
  score += Math.ceil(lengthRatio * 30);

  // Extra length bonus (up to 10 points)
  if (password.length >= rules.minLength * 2) {
    score += 10;
  }

  // Requirement bonuses (15 points each if met)
  const totalRequirements = 4; // uppercase, lowercase, number, special
  const metRequirements = totalRequirements - missingRequirements.length;
  score += (metRequirements / totalRequirements) * 60;

  return Math.min(Math.round(score), 100);
}

/**
 * Determine password strength based on missing requirements
 *
 * - WEAK: Less than 8 chars OR missing 2+ requirements
 * - FAIR: 8+ chars AND 1-2 requirements met
 * - GOOD: 12+ chars AND 3+ requirements met (missing only 1)
 * - STRONG: 16+ chars AND all requirements met
 *
 * @param password The password to evaluate
 * @param missingRequirements Array of unmet requirement identifiers
 * @returns Password strength level
 */
function calculatePasswordStrength(
  password: string,
  missingRequirements: string[]
): PasswordStrength {
  const length = password.length;

  // WEAK: too short or missing too many requirements
  if (length < 8 || missingRequirements.length >= 3) {
    return PasswordStrength.WEAK;
  }

  // FAIR: meets length but missing 1-2 requirements
  if (length < 12 || missingRequirements.length >= 1) {
    return PasswordStrength.FAIR;
  }

  // GOOD: good length and missing at most 1 special char requirement
  if (length < 16 || (missingRequirements.length === 1 && missingRequirements.includes('special'))) {
    return PasswordStrength.GOOD;
  }

  // STRONG: 16+ chars and all requirements met
  return PasswordStrength.STRONG;
}

/**
 * Password strength validator
 *
 * Validates password complexity against configurable rules.
 * Provides detailed feedback on missing requirements.
 *
 * Strength levels:
 * - WEAK (0-25): Less than 8 chars or missing 2+ requirements
 * - FAIR (26-50): 8+ chars with some requirements met
 * - GOOD (51-75): 12+ chars with most requirements met
 * - STRONG (76-100): 16+ chars with all requirements met
 *
 * Usage:
 * - Register validator: engine.registerValidator('passwordStrength', passwordStrengthValidator)
 * - Use with form field type: 'password' with validation.passwordStrength
 *
 * Configuration options (via control.config.passwordStrengthRules):
 * ```typescript
 * {
 *   minLength: 8,
 *   requireUppercase: true,
 *   requireLowercase: true,
 *   requireNumber: true,
 *   requireSpecialChar: false,
 *   specialCharPattern: '!@#$%^&*'
 * }
 * ```
 *
 * @param value The password value to validate
 * @param control The FormControl definition
 * @param context Full ValidationContext
 * @returns Error message or null if valid
 *
 * @example
 * ```typescript
 * const engine = new ValidationEngine();
 * engine.registerValidator('passwordStrength', passwordStrengthValidator);
 *
 * const result = engine.validateField(
 *   {
 *     id: 'password',
 *     label: 'Password',
 *     type: 'password',
 *     validation: { validators: ['passwordStrength'] }
 *   },
 *   'MyPassword123'
 * );
 * ```
 */
export const passwordStrengthValidator: ValidatorFn = (
  value: unknown,
  control: FormControl,
  _context: ValidationContext
): string | null => {
  // Skip validation for empty passwords
  if (typeof value !== 'string' || value === '') {
    return null;
  }

  // SECURITY: Add length check first - passwords over 1000 chars are likely attacks
  if (value.length > 1000) {
    return `${control.label || 'Password'} exceeds maximum allowed length`;
  }

  // Get configured rules or use defaults
  const rulesConfig = (control.config?.passwordStrengthRules as PasswordStrengthRules) || {};
  const rules: Required<PasswordStrengthRules> = {
    minLength: rulesConfig.minLength ?? DEFAULT_PASSWORD_RULES.minLength,
    requireUppercase: rulesConfig.requireUppercase ?? DEFAULT_PASSWORD_RULES.requireUppercase,
    requireLowercase: rulesConfig.requireLowercase ?? DEFAULT_PASSWORD_RULES.requireLowercase,
    requireNumber: rulesConfig.requireNumber ?? DEFAULT_PASSWORD_RULES.requireNumber,
    requireSpecialChar: rulesConfig.requireSpecialChar ?? DEFAULT_PASSWORD_RULES.requireSpecialChar,
    specialCharPattern: rulesConfig.specialCharPattern ?? DEFAULT_PASSWORD_RULES.specialCharPattern,
  };

  const password = value;
  const feedback: string[] = [];

  // Check minimum length
  if (password.length < rules.minLength) {
    feedback.push(`Password must be at least ${rules.minLength} characters (currently ${password.length})`);
  }

  // Check uppercase requirement - simple, fast pattern with no backtracking
  if (rules.requireUppercase && !/[A-Z]/.test(password)) {
    feedback.push('Add an uppercase letter (A-Z)');
  }

  // Check lowercase requirement - simple, fast pattern with no backtracking
  if (rules.requireLowercase && !/[a-z]/.test(password)) {
    feedback.push('Add a lowercase letter (a-z)');
  }

  // Check number requirement - simple, fast pattern with no backtracking
  if (rules.requireNumber && !/\d/.test(password)) {
    feedback.push('Add a number (0-9)');
  }

  // Check special character requirement - simplified pattern without possessive quantifiers
  // Uses character class with proper escaping instead of dynamic regex construction
  if (rules.requireSpecialChar) {
    // Build regex from the configured specialCharPattern
    const pattern = rules.specialCharPattern || '!@#$%^&*';
    const escaped = pattern.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    let hasSpecial = false;
    try {
      hasSpecial = new RegExp(`[${escaped}]`).test(password);
    } catch {
      // Fallback if pattern is invalid
      hasSpecial = /[!@#$%^&*()\-_=[\]{}|;:'",.<>?/\\~`]/.test(password);
    }
    if (!hasSpecial) {
      feedback.push(`Add a special character (${pattern})`);
    }
  }

  // If there are errors, return the first one as the validation error
  // Limit feedback to 5 items to avoid overwhelming the user
  if (feedback.length > 0) {
    const limitedFeedback = feedback.slice(0, 5);
    return `${control.label || 'Password'} is not strong enough. ${limitedFeedback.join(' ')}`;
  }

  return null;
};

/**
 * Validate password and return detailed strength analysis
 *
 * Use this function to get comprehensive password strength details
 * for displaying strength meters and detailed feedback.
 *
 * @param password The password to analyze
 * @param rules Optional password strength rules (uses defaults if not provided)
 * @returns Detailed password validation result with strength level and feedback
 *
 * @example
 * ```typescript
 * const result = validatePasswordStrength('MyPassword123');
 * // {
 * //   isValid: true,
 * //   strength: 'good',
 * //   score: 75,
 * //   feedback: [],
 * //   missingRequirements: ['special']
 * // }
 * ```
 */
export function validatePasswordStrength(
  password: string,
  rules?: PasswordStrengthRules
): PasswordValidationResult {
  // SECURITY: Add length check first - reject passwords over 1000 chars as likely attack
  if (!password || password.length > 1000) {
    return {
      isValid: false,
      strength: PasswordStrength.WEAK,
      score: 0,
      feedback: ['Password must be between 1 and 1000 characters'],
      missingRequirements: ['length'],
    };
  }

  // Merge provided rules with defaults
  const mergedRules: Required<PasswordStrengthRules> = {
    minLength: rules?.minLength ?? DEFAULT_PASSWORD_RULES.minLength,
    requireUppercase: rules?.requireUppercase ?? DEFAULT_PASSWORD_RULES.requireUppercase,
    requireLowercase: rules?.requireLowercase ?? DEFAULT_PASSWORD_RULES.requireLowercase,
    requireNumber: rules?.requireNumber ?? DEFAULT_PASSWORD_RULES.requireNumber,
    requireSpecialChar: rules?.requireSpecialChar ?? DEFAULT_PASSWORD_RULES.requireSpecialChar,
    specialCharPattern: rules?.specialCharPattern ?? DEFAULT_PASSWORD_RULES.specialCharPattern,
  };

  const result: PasswordValidationResult = {
    isValid: true,
    strength: PasswordStrength.WEAK,
    score: 0,
    feedback: [],
    missingRequirements: [],
  };

  // Check minimum length
  if (password.length < mergedRules.minLength) {
    result.feedback.push(`Password must be at least ${mergedRules.minLength} characters (currently ${password.length})`);
    result.missingRequirements.push('length');
    result.isValid = false;
  }

  // Check uppercase requirement - simple pattern with no backtracking risk
  if (mergedRules.requireUppercase && !/[A-Z]/.test(password)) {
    result.feedback.push('Add an uppercase letter (A-Z)');
    result.missingRequirements.push('uppercase');
    result.isValid = false;
  }

  // Check lowercase requirement - simple pattern with no backtracking risk
  if (mergedRules.requireLowercase && !/[a-z]/.test(password)) {
    result.feedback.push('Add a lowercase letter (a-z)');
    result.missingRequirements.push('lowercase');
    result.isValid = false;
  }

  // Check number requirement - simple pattern with no backtracking risk
  if (mergedRules.requireNumber && !/\d/.test(password)) {
    result.feedback.push('Add a number (0-9)');
    result.missingRequirements.push('number');
    result.isValid = false;
  }

  // Check special character requirement - simplified pattern
  // Uses static character class instead of dynamic regex to avoid ReDoS
  if (mergedRules.requireSpecialChar) {
    const specialChars = /[!@#$%^&*()\-_=[\]{}|;:'",.<>?/\\~`]/;
    if (!specialChars.test(password)) {
      result.feedback.push(`Add a special character (${mergedRules.specialCharPattern})`);
      result.missingRequirements.push('special');
      result.isValid = false;
    }
  }

  // Calculate strength and score
  result.strength = calculatePasswordStrength(password, result.missingRequirements);
  result.score = calculatePasswordScore(password, result.missingRequirements, mergedRules);

  return result;
}

/**
 * Registry of all built-in validators
 *
 * Core validators that handle standard validation rules.
 * Custom validators can be registered via ValidationEngine.registerValidator().
 */
export const BUILT_IN_VALIDATORS: Record<string, ValidatorFn> = {
  required: requiredValidator,
  minLength: minLengthValidator,
  maxLength: maxLengthValidator,
  min: minValidator,
  max: maxValidator,
  pattern: patternValidator,
  email: emailValidator,
  url: urlValidator,
  custom: customMessageValidator,
  passwordConfirm: passwordConfirmValidator,
  dateRange: dateRangeValidator,
  dependentRequired: dependentRequiredValidator,
  passwordStrength: passwordStrengthValidator,
};
